/**
 * Unified Analytics Service
 *
 * Single entry point for all analytics. Each function fires all destinations in order:
 *   1. sGTM (dataLayer.push) — fires first
 *   2. PostHog
 *   3. Matomo (production only — no-op if VITE_MATOMO_URL / VITE_MATOMO_SITE_ID not set)
 *
 * All calls are wrapped in safeTrack() — failures are isolated and logged as warnings.
 * sGTM fires first by position, guaranteeing revenue tracking even if PostHog/Matomo throw.
 *
 * Feature files import ONLY from this module, never directly from analytics.ts or posthog.ts.
 */

import {
  trackLogin,
  trackTrialCreated,
  trackFirstDevicePaired,
  trackFirstAssetCreated,
  trackNewAssetCreated,
  trackFirstPlaylistCreated,
  trackNewPlaylistCreated,
  trackFirstScheduleCreated,
  trackNewScheduleCreated,
  trackFirstTeamMemberInvited,
  trackNewTeamMemberInvited,
  trackFirstTemplateUsed,
  trackNewTemplateUsed,
  trackFirstDeviceOrdered,
  trackNewDeviceOrdered,
  trackFirstSubscription,
  trackSubUpdate,
  trackWinbackReactivated,
} from '@/lib/analytics';
import {
  posthog,
  trackEvent,
  shouldFireIdentify,
  shouldFireGroup,
  clearIdentityCache,
} from '@/lib/posthog';
import { visitorIdPersonProperties } from '@/lib/posthog-visitor';
import { getInitialUtms } from '@/lib/utm-capture';
import { matomoIdentify, matomoTrackEvent, matomoReset } from '@/lib/matomo';
import { getConsumedIntent } from '@/lib/app-intent';
import { useAuthStore } from '@/stores/auth-store';

// Re-export for call sites that need the persistence hook
export { usePersistFirstEventFlag } from '@/lib/analytics';

// ── Core ─────────────────────────────────────────────────────────────────────

function safeTrack(name: string, fn: () => void): void {
  try {
    fn();
  } catch (e) {
    console.warn(`[analytics:${name}]`, e);
  }
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export function analyticsLogin(params: { method: string; isMfa?: boolean }): void {
  safeTrack('sgtm', () => trackLogin());
  safeTrack('posthog', () =>
    trackEvent('user_logged_in', { method: params.method, is_mfa: params.isMfa ?? false }),
  );
}

/**
 * Fires on session resume (auth state restored without a fresh sign-in).
 * Mirrors scio1 `onlyTrackingLogInResume` (analytics-service.js:45). Only
 * fires GTM — does NOT re-identify PostHog/Matomo, since the user is the
 * same identity that's already cached.
 */
export function analyticsLoginResume(): void {
  safeTrack('sgtm', () => trackLogin());
}

/**
 * Fires once per account when a trial is started — GTM only. Mirrors
 * scio1 `trackingTrialCreated` (analytics-service.js:160), which fires
 * ONLY GTM `trialCreated` (no Matomo, no other destination). PostHog
 * `trial_started` is already emitted from `analyticsSignup` at signup
 * time, so we deliberately skip PostHog here to avoid duplicates.
 *
 * Caller MUST gate on `company.payment.trialStartEventFired === 'NOT_TRIGGERED'`
 * and call the `triggerTrialStartEvent` mutation immediately after to flip the
 * flag — otherwise the event fires on every reload.
 */
export function analyticsTrialCreated(): void {
  safeTrack('sgtm', () => trackTrialCreated());
}

export function analyticsIdentify(params: {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  rootRole?: string;
  accountId?: string;
  teamId?: string;
  plan?: string;
  isTrial?: boolean;
  screenCount?: number;
  companyName?: string;
  companyScioVersion?: number;
  /** ISO timestamp of the user's account creation. Persisted as a person
   *  property so PostHog experiment cohorts can filter on
   *  `signup_date >= <experiment_start>` to expose only new signups. */
  signupDate?: string;
  /** Where the user signed up — defaults to 'scio3' for this app. Lets PostHog
   *  cohorts isolate scio3 traffic when other surfaces (smallapps, sites)
   *  start identifying users too. */
  signupSource?: string;
  /** ISO timestamp of the COMPANY/account creation (company.createdAt). Account-level
   *  counterpart to the per-user `signup_date`, so analysis can distinguish when the
   *  account was created vs when this individual user joined it. */
  companySignupDate?: string;
}): void {
  safeTrack('posthog', () => {
    const personPayload = {
      email: params.email,
      name: [params.firstName, params.lastName].filter(Boolean).join(' '),
      role: params.rootRole,
      plan: params.plan,
      platform: params.companyScioVersion != null ? `scio${params.companyScioVersion}` : undefined,
      ...(params.signupDate ? { signup_date: params.signupDate } : {}),
      ...(params.signupSource ? { signup_source: params.signupSource } : {}),
      ...(params.companySignupDate ? { company_signup_date: params.companySignupDate } : {}),
    };
    // Register the cohort properties for PostHog flag evaluation BEFORE identify
    // (no reload here), so the identify-triggered /flags reload already includes
    // them. Otherwise the first identified flag eval runs without them → PostHog
    // returns "No matching condition set" and drops fresh signups (especially
    // social, which reads flags on exactly that first identify reload) out of any
    // signup-gated cohort.
    //
    // `platform` is part of the cohort condition too, but it only reaches flag
    // evaluation when registered HERE. The value passed to identify() below is a
    // person property on the $identify event, which PostHog does NOT feed into the
    // immediate flag reload — so mirror it into the flag properties so the
    // condition (`platform = scio3`) can match on that first identified eval.
    const flagProps: Record<string, string> = {};
    if (personPayload.platform) flagProps.platform = personPayload.platform;
    if (params.signupDate) flagProps.signup_date = params.signupDate;
    if (params.signupSource) flagProps.signup_source = params.signupSource;
    if (Object.keys(flagProps).length > 0) {
      posthog.setPersonPropertiesForFlags(flagProps, false);
    }

    const currentDistinctId = posthog.get_distinct_id();
    if (
      shouldFireIdentify({
        userId: params.userId,
        payload: personPayload,
        currentDistinctId,
      })
    ) {
      posthog.identify(params.userId, personPayload, visitorIdPersonProperties());
      // Only push initial UTMs on TRUE first identification (anonymous → real user).
      // Existing users don't get retroactive attribution from current cookie state.
      if (currentDistinctId !== params.userId) {
        const initialUtms = getInitialUtms();
        if (Object.keys(initialUtms).length > 0) {
          posthog.setPersonProperties({}, initialUtms);
        }
      }
    }

    if (params.accountId) {
      const groupPayload = {
        name: params.companyName,
        plan: params.plan,
        screen_count: params.screenCount,
      };
      if (
        shouldFireGroup({
          accountId: params.accountId,
          payload: groupPayload,
          currentGroups: posthog.getGroups(),
        })
      ) {
        posthog.group('company', params.accountId, groupPayload);
      }
    }

    posthog.register({
      plan_tier: params.plan,
      user_role: params.rootRole,
      is_trial: params.isTrial ?? false,
      is_internal: params.email?.endsWith('@optisigns.com') ?? false,
      team_id: params.teamId,
      screen_count: params.screenCount,
    });
  });
  if (params.userId && params.email && params.accountId) {
    safeTrack('matomo', () => matomoIdentify(params.userId, params.email, params.accountId ?? ''));
  }
}

export function analyticsLogout(): void {
  safeTrack('posthog', () => {
    trackEvent('user_logged_out');
    clearIdentityCache();
    // posthog.reset() clears persistence (incl. any $override_feature_flags), so
    // stale flag state can't leak into the next user who signs in on this
    // browser. No separate overrideFeatureFlags(false) needed.
    posthog.reset();
  });
  safeTrack('matomo', () => matomoReset());
}

// ── Signup ────────────────────────────────────────────────────────────────────

export function analyticsSignup(params: {
  method: string;
  source?: string;
  referrer?: string;
  plan?: string;
  /**
   * Referral code captured from the `/r/:code` vanity cookie at signup, when the
   * referee arrived via a referral link. Present → the signup is referral-driven
   * and the analytics events carry `is_referral: true` + the code so referral
   * acquisition is measurable in PostHog. Omitted for organic signups.
   */
  referralCode?: string;
}): void {
  const isReferral = Boolean(params.referralCode);
  safeTrack('posthog', () => {
    trackEvent('account_created', {
      method: params.method,
      source: params.source,
      referrer: params.referrer,
      plan: params.plan,
      is_referral: isReferral,
      referral_code: params.referralCode,
    });
    trackEvent('user_signed_up', {
      method: params.method,
      source: params.source,
      referrer: params.referrer,
      is_referral: isReferral,
      referral_code: params.referralCode,
    });
    trackEvent('trial_started', { plan: params.plan, trial_days: 14 });
  });
  safeTrack('matomo', () => matomoTrackEvent('Trial Started', 'Trial Started'));
}

// ── Winback Reactivation ──────────────────────────────────────────────────────

/**
 * Fires once when a lapsed/free-downgraded account claims the OptiReach winback
 * 30-day trial (`reactivateWithTrial` success). Deliberately DISTINCT from
 * `analyticsTrialCreated` — reactivation leaves `trialStartEventFired`
 * untouched server-side, so the trial-created GTM conversion must not re-fire.
 * `campaign` is the OptiReach `utm_campaign` (the cross-repo stats join key).
 */
export function analyticsReactivated(params: { campaign: string }): void {
  safeTrack('sgtm', () => trackWinbackReactivated({ campaign: params.campaign }));
  safeTrack('posthog', () => trackEvent('winback_reactivated', { campaign: params.campaign }));
  safeTrack('matomo', () => matomoTrackEvent('Winback', 'Winback Reactivated', params.campaign));
}

// ── Dashboard fast path attribution ───────────────────────────────────────────

/**
 * Attribution the dashboard fast path attaches to activation events (spec 08 FR-22c) — so the
 * funnel is answerable without a session-stitching join. One helper so `screen_paired`,
 * `screen_pairing_started` and `asset_created` can never drift apart; read here rather than at
 * the call sites so the features stay out of the intent's business. Empty object when this tab
 * never consumed an intent — the keys are ABSENT, not null, so a non-cohort event keeps its
 * original shape.
 */
export function appIntentProps():
  | {
      is_app_intent: true;
      app_intent_app: string;
      app_intent_source: string;
      app_intent_age_seconds: number;
    }
  | Record<string, never> {
  const consumed = getConsumedIntent();
  if (!consumed) return {};
  return {
    is_app_intent: true,
    app_intent_app: consumed.app,
    app_intent_source: consumed.source ?? 'unknown',
    app_intent_age_seconds: Math.max(0, Math.floor(Date.now() / 1000) - consumed.at),
  };
}

// ── Screens ───────────────────────────────────────────────────────────────────

export function analyticsScreenPaired(params: { screenId?: string; osType?: string }): void {
  const company = useAuthStore.getState().company;
  const isFirst = company ? !company.payment?.firstDevicePaired : false;
  safeTrack('sgtm', () => trackFirstDevicePaired());
  safeTrack('posthog', () =>
    trackEvent('screen_paired', {
      screen_id: params.screenId,
      os_type: params.osType,
      is_first: isFirst,
      ...appIntentProps(),
    }),
  );
  if (isFirst) {
    safeTrack('matomo', () =>
      matomoTrackEvent('First Screen Assignment', 'First Screen Assignment Completed'),
    );
  }
}

/**
 * The pair dialog opened — the step between "Next: set up a screen" and `screen_paired`, which is
 * where the fast-path funnel leaks. PostHog only: there is no sGTM/Matomo conversion for an
 * intent. `is_first` is derived exactly as `analyticsScreenPaired` does (false while the company
 * has not loaded) so the two events are directly comparable.
 */
export function analyticsScreenPairingStarted(): void {
  const company = useAuthStore.getState().company;
  const isFirst = company ? !company.payment?.firstDevicePaired : false;
  safeTrack('posthog', () =>
    trackEvent('screen_pairing_started', { is_first: isFirst, ...appIntentProps() }),
  );
}

export function analyticsContentPushed(params: {
  contentType?: string;
  contentId?: string;
  screenCount?: number;
  isFirst?: boolean;
}): void {
  safeTrack('posthog', () =>
    trackEvent('content_pushed', {
      content_type: params.contentType,
      content_id: params.contentId,
      screen_count: params.screenCount ?? 0,
      is_first: params.isFirst ?? false,
    }),
  );
}

// ── Assets ────────────────────────────────────────────────────────────────────

/**
 * `uploadMethod` (spec 08 FR-22c) is the surface the asset was created from — the SmallApp editor
 * leg never fired this event at all before round 6, so without it the funnel cannot tell an
 * editor create from a file upload. PostHog-only: the sGTM legs keep their original payload.
 */
export function analyticsAssetCreated(
  params: {
    type: string;
    fileType?: string;
    appType?: string;
    uploadMethod: 'asset-form' | 'file-upload' | 'smallapp-editor' | 'designer-template' | 'feed';
  },
  onFirst?: () => void,
): void {
  const { uploadMethod, ...sgtmParams } = params;
  let isFirst = false;
  safeTrack('sgtm', () => {
    const fired = trackFirstAssetCreated(sgtmParams);
    isFirst = fired;
    if (fired) onFirst?.();
    trackNewAssetCreated(sgtmParams);
  });
  safeTrack('posthog', () =>
    trackEvent('asset_created', {
      asset_type: params.type,
      file_type: params.fileType,
      app_type: params.appType,
      is_first: isFirst,
      upload_method: uploadMethod,
      ...appIntentProps(),
    }),
  );
}

// ── Playlists ─────────────────────────────────────────────────────────────────

export function analyticsPlaylistCreated(
  params: { itemCount: number; totalDuration: number; hasTransitions: boolean },
  onFirst?: () => void,
): void {
  let isFirst = false;
  safeTrack('sgtm', () => {
    const fired = trackFirstPlaylistCreated({
      itemCount: params.itemCount,
      totalDuration: params.totalDuration,
    });
    isFirst = fired;
    if (fired) onFirst?.();
    trackNewPlaylistCreated({ itemCount: params.itemCount, totalDuration: params.totalDuration });
  });
  safeTrack('posthog', () =>
    trackEvent('playlist_created', {
      item_count: params.itemCount,
      total_duration_seconds: params.totalDuration,
      has_transitions: params.hasTransitions,
      is_first: isFirst,
    }),
  );
}

// ── Schedules ─────────────────────────────────────────────────────────────────

export function analyticsScheduleCreated(onFirst?: () => void): void {
  let isFirst = false;
  safeTrack('sgtm', () => {
    const fired = trackFirstScheduleCreated();
    isFirst = fired;
    if (fired) onFirst?.();
    trackNewScheduleCreated();
  });
  safeTrack('posthog', () => trackEvent('schedule_created', { is_first: isFirst }));
}

// ── Search ────────────────────────────────────────────────────────────────────

export function analyticsSearchPerformed(params: {
  queryLength: number;
  entityType: 'screens' | 'assets' | 'playlists' | 'schedules';
}): void {
  safeTrack('posthog', () =>
    trackEvent('search_performed', {
      query_length: params.queryLength,
      entity_type: params.entityType,
    }),
  );
}

/**
 * Fires when the full global-search results page (`/app/search`) is mounted
 * with a valid query. `source` distinguishes how the user arrived so we can
 * measure which entry points drive usage.
 */
export function analyticsSearchPageOpened(params: {
  queryLength: number;
  source: 'dropdown-enter' | 'dropdown-see-all' | 'direct-url';
}): void {
  safeTrack('posthog', () =>
    trackEvent('search_page_opened', {
      query_length: params.queryLength,
      source: params.source,
    }),
  );
}

/**
 * Fires when a user clicks a result row on the full global-search page.
 * `position` is the row's index within its section (0-based).
 */
export function analyticsSearchPageResultClicked(params: {
  entityType: string;
  position: number;
}): void {
  safeTrack('posthog', () =>
    trackEvent('search_page_result_clicked', {
      entity_type: params.entityType,
      position: params.position,
    }),
  );
}

// ── Teams ─────────────────────────────────────────────────────────────────────

export function analyticsTeamSwitched(params: { fromTeamId?: string; toTeamId: string }): void {
  safeTrack('posthog', () => {
    trackEvent('team_switched', { from_team_id: params.fromTeamId, to_team_id: params.toTeamId });
    posthog.register({ team_id: params.toTeamId });
  });
}

/**
 * Fires when a team member is invited. Mirrors scio1
 * `analyticTeamMemberInvited` (analytics-service.js:555) which dispatches
 * to `firstTeamMemberInvited` or `newTeamMemberInvited` based on the
 * userSettings flag. GTM gets both first+new variants (first only once,
 * new always); PostHog gets the single `team_member_invited` event.
 *
 * `onFirst` callback (optional): invoked when the first-fire path
 * triggers, so the caller can persist the flag via
 * `usePersistFirstEventFlag('firstTeamMemberInvited')`.
 */
export function analyticsTeamMemberInvited(
  params: {
    email?: string;
    name?: string;
    teamId?: string;
    role?: string;
  },
  onFirst?: () => void,
): void {
  let isFirst = false;
  safeTrack('sgtm', () => {
    const fired = trackFirstTeamMemberInvited({ email: params.email });
    isFirst = fired;
    if (fired) onFirst?.();
    trackNewTeamMemberInvited({ email: params.email });
  });
  safeTrack('posthog', () =>
    trackEvent('team_member_invited', {
      email: params.email,
      name: params.name,
      team_id: params.teamId,
      role: params.role,
      is_first: isFirst,
    }),
  );
}

// ── Templates (Designer/Canvas) ──────────────────────────────────────────────

/**
 * Fires when a Canvas/Designer template is applied. Mirrors scio1
 * `analyticTemplateUsed` (analytics-service.js:630). `onFirst` callback
 * lets the caller persist `firstTemplateUsed` via usePersistFirstEventFlag.
 */
export function analyticsTemplateUsed(templateId: string, onFirst?: () => void): void {
  if (!templateId) return;
  safeTrack('sgtm', () => {
    const fired = trackFirstTemplateUsed(templateId);
    if (fired) onFirst?.();
    trackNewTemplateUsed(templateId);
  });
}

// ── Device Orders (Hardware Checkout) ────────────────────────────────────────

/**
 * Fires after a successful hardware device order checkout. Mirrors scio1
 * `analyticDeviceOrdered` (analytics-service.js:709). `onFirst` callback
 * lets the caller persist `firstDeviceOrdered` via usePersistFirstEventFlag.
 */
export function analyticsDeviceOrdered(
  params: {
    productName?: string;
    productPrice?: number;
    quantity?: number;
    totalAmount?: number;
  },
  onFirst?: () => void,
): void {
  safeTrack('sgtm', () => {
    const fired = trackFirstDeviceOrdered(params);
    if (fired) onFirst?.();
    trackNewDeviceOrdered(params);
  });
}

export function analyticsFolderCreated(params: { featureType: string }): void {
  safeTrack('posthog', () => trackEvent('folder_created', { feature_type: params.featureType }));
}

// ── Get Started ───────────────────────────────────────────────────────────────

export function analyticsGetStartedStep(params: { stepId: string; stepNumber: number }): void {
  safeTrack('posthog', () =>
    trackEvent('get_started_step_completed', {
      step_id: params.stepId,
      step_number: params.stepNumber,
    }),
  );
}

export function analyticsGetStartedCompleted(params: { stepsCompleted: number }): void {
  safeTrack('posthog', () =>
    trackEvent('get_started_completed', { steps_completed: params.stepsCompleted }),
  );
}

// ── OptiBot ───────────────────────────────────────────────────────────────────

export function analyticsOptibotStarted(params: {
  sourcePage: string;
  entryPoint?: `contextual:${string}`;
  screenPrompt?: string;
}): void {
  safeTrack('posthog', () =>
    trackEvent('optibot_conversation_started', {
      source_page: params.sourcePage,
      ...(params.entryPoint ? { entry_point: params.entryPoint } : {}),
      ...(params.screenPrompt ? { screen_prompt: params.screenPrompt } : {}),
    }),
  );
}

export function analyticsOptibotMessage(params: {
  messageLength: number;
  suggestionId?: string;
}): void {
  safeTrack('posthog', () =>
    trackEvent('optibot_message_sent', {
      message_length: params.messageLength,
      source: params.suggestionId ? 'suggestion' : 'typed',
      ...(params.suggestionId ? { suggestion_id: params.suggestionId } : {}),
    }),
  );
}

export function analyticsOptibotFeedback(params: { rating: 'positive' | 'negative' }): void {
  safeTrack('posthog', () => trackEvent('optibot_feedback_given', { rating: params.rating }));
}

// ── Navigation & Engagement ───────────────────────────────────────────────────

export function analyticsNotificationClicked(params: { notificationType: string }): void {
  safeTrack('posthog', () =>
    trackEvent('notification_clicked', { notification_type: params.notificationType }),
  );
}

export function analyticsUpgradeCta(params: { ctaLocation: string; currentPlan?: string }): void {
  safeTrack('posthog', () =>
    trackEvent('upgrade_cta_clicked', {
      cta_location: params.ctaLocation,
      current_plan: params.currentPlan,
    }),
  );
}

// ── Devices ────────────────────────────────────────────────────────────────────

/**
 * Fires when a user opens the Order Devices page. `surface` tags where they
 * clicked from (currently the top nav) so order-device engagement can be sliced
 * by entry point.
 */
/** `surface` distinguishes the entry points so we can see which one users actually take: the
 *  top-nav cart vs the Screens toolbar button (restored alongside it). */
export function analyticsOrderDevicesOpened(surface: 'top-nav' | 'screens-toolbar'): void {
  safeTrack('posthog', () => trackEvent('order_devices_opened', { surface }));
}

// ── Onboarding ───────────────────────────────────────────────────────────────

/**
 * Funnel marker for the onboarding survey. Fires once when the survey surface
 * first appears post-signup — a deterministic "user reached onboarding" event to
 * gate funnels on.
 */
export function analyticsOnboardingStarted(): void {
  safeTrack('posthog', () => trackEvent('onboarding_started', {}));
}

/**
 * Fires each time the user advances PAST a step (Q1 auto-advance + every
 * Continue click except the final submit, which is captured by
 * `onboarding_completed`). Gives a per-step funnel so we can see WHICH question
 * loses people. `step` is the step id (e.g. 'q2b'); `stepNumber` is 1-based.
 * `onboarding_variant` auto-attaches, so the funnel can be sliced per arm.
 */
export function analyticsOnboardingStepCompleted(params: {
  step: string;
  stepIndex: number;
  stepNumber: number;
  totalSteps: number;
}): void {
  safeTrack('posthog', () =>
    trackEvent('onboarding_step_completed', {
      step: params.step,
      step_index: params.stepIndex,
      step_number: params.stepNumber,
      total_steps: params.totalSteps,
    }),
  );
}

export function analyticsOnboardingCompleted(params: {
  /** Q1a signage experience. Optional — the embedded welcome-screen survey doesn't ask it. */
  experience?: string;
  audience: string;
  useCaseCount: number;
  inviteCount: number;
}): void {
  safeTrack('posthog', () =>
    trackEvent('onboarding_completed', {
      ...(params.experience && { signage_experience: params.experience }),
      audience: params.audience,
      use_case_count: params.useCaseCount,
      invite_count: params.inviteCount,
    }),
  );
}

/**
 * Fires when the user dismisses the entire survey via the Skip link. `step` is
 * where they bailed and `questionsAnswered` is how many questions they'd
 * answered before skipping — together these answer "who skipped immediately"
 * vs "who answered some then skipped". `audience`/`useCaseCount` snapshot any
 * partial answers. `onboarding_variant` auto-attaches for per-arm comparison.
 */
export function analyticsOnboardingSkipped(params: {
  step: string;
  stepIndex: number;
  stepNumber: number;
  totalSteps: number;
  questionsAnswered: number;
  audience: string | null;
  useCaseCount: number;
}): void {
  safeTrack('posthog', () =>
    trackEvent('onboarding_skipped', {
      step: params.step,
      step_index: params.stepIndex,
      step_number: params.stepNumber,
      total_steps: params.totalSteps,
      questions_answered: params.questionsAnswered,
      audience: params.audience ?? 'none',
      use_case_count: params.useCaseCount,
    }),
  );
}

export function analyticsOnboardingSubmitFailed(): void {
  safeTrack('posthog', () => trackEvent('onboarding_submit_failed', {}));
}

// ── Rooms / MDM Connect ──────────────────────────────────────────────────────
//
// Rooms funnel: connect → activate → assign. PostHog-only (no sGTM/Matomo —
// rooms has no ad-conversion tags). `vendor` is the platform id
// (zoom-rooms | teams-rooms | webex-rooms | google-meet); `method` is the
// connect mechanism (oauth | server-app | service-principal | service-account |
// signage-url | control-hub).

export function analyticsRoomConnectStarted(params: { vendor: string; method: string }): void {
  safeTrack('posthog', () =>
    trackEvent('room_connect_started', { vendor: params.vendor, method: params.method }),
  );
}

export function analyticsRoomConnectCompleted(params: { vendor: string; method: string }): void {
  safeTrack('posthog', () =>
    trackEvent('room_connect_completed', { vendor: params.vendor, method: params.method }),
  );
}

export function analyticsRoomConnectFailed(params: {
  vendor: string;
  method: string;
  errorCode?: string;
}): void {
  safeTrack('posthog', () =>
    trackEvent('room_connect_failed', {
      vendor: params.vendor,
      method: params.method,
      error_code: params.errorCode,
    }),
  );
}

/**
 * Fires once per successful activateSignage call (single or bulk).
 * `is_first` mirrors the FDP flag the backend writes for room activations
 * (`device-platforms.service.ts updateFirstDevicePaired`) — read from the
 * pre-activation company snapshot, same pattern as `analyticsScreenPaired`.
 * Without this, room-driven FDP is invisible to PostHog (`screen_paired`
 * only fires from the normal pairing flow).
 */
export function analyticsRoomSignageActivated(params: {
  vendor: string;
  count: number;
  failedCount: number;
}): void {
  const company = useAuthStore.getState().company;
  const isFirst = company ? !company.payment?.firstDevicePaired : false;
  safeTrack('posthog', () =>
    trackEvent('room_signage_activated', {
      vendor: params.vendor,
      count: params.count,
      failed_count: params.failedCount,
      is_first: isFirst,
    }),
  );
}

export function analyticsRoomContentAssigned(params: {
  vendor: string;
  contentType?: string;
}): void {
  safeTrack('posthog', () =>
    trackEvent('room_content_assigned', {
      vendor: params.vendor,
      content_type: params.contentType,
    }),
  );
}

// ── Errors ────────────────────────────────────────────────────────────────────

export function analyticsError(params: {
  errorType: string;
  errorMessage: string;
  page: string;
  /** Minified JS stack. Without it a crash report is an error message and nothing else,
   *  which is why #2260 had to be root-caused by reading code. Omitted when the caller
   *  has no stack (a thrown non-Error), and omitted from the event rather than sent empty. */
  errorStack?: string;
  /** React component stack from `componentDidCatch` — names the component that threw. */
  componentStack?: string;
}): void {
  safeTrack('posthog', () =>
    trackEvent('error_encountered', {
      error_type: params.errorType,
      error_message: params.errorMessage.slice(0, 200),
      page: params.page,
      ...(params.errorStack ? { error_stack: params.errorStack.slice(0, 2000) } : {}),
      ...(params.componentStack ? { component_stack: params.componentStack.slice(0, 2000) } : {}),
    }),
  );
}

// ── Subscription ──────────────────────────────────────────────────────────────

export function analyticsSubscriptionCreated(params: {
  planId: string;
  noOfDevices: number;
  currency: string;
  stripeDuration: string;
  stripePlanPrice: number;
  stripeDiscount?: number;
  /** Surface that initiated the purchase (e.g. 'room-activate'). Optional —
   *  absent = unattributed. PostHog-only; sGTM payload must not change. */
  purchaseSource?: string;
}): void {
  safeTrack('sgtm', () => trackFirstSubscription(params));
  safeTrack('posthog', () =>
    trackEvent('first_subscription', {
      plan_id: params.planId,
      screen_count: params.noOfDevices,
      currency: params.currency,
      duration: params.stripeDuration,
      plan_price: params.stripePlanPrice,
      discount: params.stripeDiscount ?? 0,
      ...(params.purchaseSource ? { purchase_source: params.purchaseSource } : {}),
    }),
  );
}

export function analyticsSubscriptionUpdated(params: {
  planId: string;
  /** Delta (newScreens - currentScreens). Matches scio1 subUpdate semantics — sent to sGTM. */
  noOfDevices: number;
  /** New absolute screen count AFTER the change — sent to PostHog as screen_count. */
  screenCount: number;
  currency: string;
  stripeDuration: string;
  stripePlanPrice: number;
  stripeDiscount?: number;
  /** Surface that initiated the purchase (e.g. 'room-activate'). Optional —
   *  absent = unattributed. PostHog-only; sGTM payload must not change. */
  purchaseSource?: string;
}): void {
  safeTrack('sgtm', () => trackSubUpdate(params));
  safeTrack('posthog', () =>
    trackEvent('sub_update', {
      plan_id: params.planId,
      screen_count: params.screenCount,
      screen_count_delta: params.noOfDevices,
      currency: params.currency,
      duration: params.stripeDuration,
      plan_price: params.stripePlanPrice,
      discount: params.stripeDiscount ?? 0,
      ...(params.purchaseSource ? { purchase_source: params.purchaseSource } : {}),
    }),
  );
}

// ── Referral (Refer & Earn) ────────────────────────────────────────────────────

/**
 * Referral surface where an invite/copy action happened — the floating pill's
 * modal vs the full `/app/s/referral` page. Lets one event answer "are people
 * sharing from the pop-up or the dedicated page?" without two event names.
 */
export type ReferralSurface = 'pill_modal' | 'page';

/** The launcher pill was shown to the user (impression). Fires once per mount. */
export function analyticsReferralWidgetShown(): void {
  safeTrack('posthog', () => trackEvent('referral_widget_shown', {}));
}

/** User opened the referral modal by clicking the floating pill. */
export function analyticsReferralPillOpened(): void {
  safeTrack('posthog', () => trackEvent('referral_pill_opened', {}));
}

/** User dismissed the floating referral pill. */
export function analyticsReferralPillDismissed(): void {
  safeTrack('posthog', () => trackEvent('referral_pill_dismissed', {}));
}

/**
 * The referral modal auto-opened at an expansion moment (server-driven prompt).
 * `promptType` is the engine's event type — `'first_paid'` / `'first_expansion'`
 * — so the two nudges can be compared.
 */
export function analyticsReferralModalAutoOpened(params: { promptType?: string }): void {
  safeTrack('posthog', () =>
    trackEvent('referral_modal_auto_opened', {
      ...(params.promptType ? { prompt_type: params.promptType } : {}),
    }),
  );
}

/** User sent email invite(s). `count` = number of addresses in the send. */
export function analyticsReferralInviteSent(params: {
  surface: ReferralSurface;
  count: number;
}): void {
  safeTrack('posthog', () =>
    trackEvent('referral_invite_sent', { surface: params.surface, invite_count: params.count }),
  );
}

/** User copied their referral link. */
export function analyticsReferralLinkCopied(params: { surface: ReferralSurface }): void {
  safeTrack('posthog', () => trackEvent('referral_link_copied', { surface: params.surface }));
}

/** User clicked the "Refer & Earn" promo card in the account dropdown menu. */
export function analyticsReferralMenuClicked(): void {
  safeTrack('posthog', () => trackEvent('referral_menu_clicked', {}));
}

/** User viewed the full Refer & Earn page (`/app/s/referral`). Fires once per mount. */
export function analyticsReferralPageViewed(): void {
  safeTrack('posthog', () => trackEvent('referral_page_viewed', {}));
}

// ── Dashboard-app intent (`/signUp?app=` fast path) ───────────────────────

/** Fired by AppShell when `/signUp?app=<allowlisted>` drops a valid intent cookie. */
export function analyticsAppIntentCaptured(params: { app: string; source?: string }): void {
  safeTrack('posthog', () =>
    trackEvent('app_intent_captured', {
      app: params.app,
      source: params.source ?? 'unknown',
    }),
  );
}

/**
 * Fired once by AppIntentLauncher when the intent is consumed and the PRE-HOOK modal
 * is shown — i.e. the moment the fast path becomes visible to the visitor. Only fires
 * when the account actually has an AppMeta for the intended app; otherwise there is no
 * pre-hook and `app_intent_modal_opened` fires with `app_found: false` instead.
 */
export function analyticsAppIntentPrehookShown(params: {
  app: string;
  source?: string;
  intentAgeSeconds?: number;
  surveyAutoSubmitted: boolean;
}): void {
  safeTrack('posthog', () =>
    trackEvent('app_intent_prehook_shown', {
      app: params.app,
      source: params.source ?? 'unknown',
      intent_age_seconds: params.intentAgeSeconds ?? null,
      survey_auto_submitted: params.surveyAutoSubmitted,
    }),
  );
}

/** Fired when the visitor answers the pre-hook with "Not now". The intent cookie is
 *  already spent at this point, so this is a terminal outcome for that intent. */
export function analyticsAppIntentPrehookDismissed(params: { app: string; source?: string }): void {
  safeTrack('posthog', () =>
    trackEvent('app_intent_prehook_dismissed', {
      app: params.app,
      source: params.source ?? 'unknown',
    }),
  );
}

/**
 * Fired when the visitor presses "Connect <App>" on the pre-hook and the launcher hands off
 * to that app's add-modal (or, with `app_found: false`, that no modal could be opened at all
 * because the account has no AppMeta for the intent).
 *
 * READ THIS BEFORE BUILDING A FUNNEL ON IT: it is a CTA-press signal, not proof the editor
 * rendered. The gates inside `openAppModal` (shared-folder confirm, per-app creation limit,
 * plan lock) run after the emit and can divert to an upgrade or limit modal instead, so a
 * small tail of these events has no editor behind it. See `useAppIntentLaunch`'s docstring
 * for why the exact signal (the smallapp editor store's open transition) was rejected.
 *
 * NOTE for anyone reading a PostHog funnel built before round 3: the event kept its
 * name but narrowed its meaning. It used to fire the instant the intent was consumed;
 * `app_intent_prehook_shown` is now that moment, and this event is one click later.
 */
export function analyticsAppIntentModalOpened(params: {
  app: string;
  source?: string;
  intentAgeSeconds?: number;
  surveyAutoSubmitted: boolean;
  appFound: boolean;
}): void {
  safeTrack('posthog', () =>
    trackEvent('app_intent_modal_opened', {
      app: params.app,
      source: params.source ?? 'unknown',
      intent_age_seconds: params.intentAgeSeconds ?? null,
      survey_auto_submitted: params.surveyAutoSubmitted,
      app_found: params.appFound,
    }),
  );
}

/**
 * Fired when the visitor leaves an app-intent add-modal through the RELABELLED footer button
 * ("Next: set up a screen"), which only exists once a save has landed in that editor session
 * (FR-12). A plain "Close" — no intent, or an intent with nothing saved — fires nothing, so
 * this event counts visitors who configured their dashboard AND accepted the nudge towards
 * screen setup, not everyone who dismissed the dialog.
 */
export function analyticsAppIntentNextStepClicked(params: { app: string; source?: string }): void {
  safeTrack('posthog', () =>
    trackEvent('app_intent_next_step_clicked', {
      app: params.app,
      source: params.source ?? 'unknown',
    }),
  );
}
